export const AUTO_SELECT_LOCALE_CURRENCY_BY_IP_KEY = 'autoSelectLocaleCurrencyByIp'
export const IP_LOCALE_API_URL_KEY = 'ipLocaleApiUrl'
export const DEFAULT_IP_LOCALE_API_URL = 'https://api.ip.sb/geoip/'
export const LANGUAGE_CACHE_KEY = 'i18n_current_locale'
export const CURRENCY_CACHE_KEY = 'currentCurrency'

const activeCountryCodeRequests = new Map()

function parseRecord(value) {
  if (typeof value === 'string') {
    try {
      const parsed = JSON.parse(value)
      return parsed && typeof parsed === 'object' && !Array.isArray(parsed)
        ? parsed
        : {}
    }
    catch {
      return {}
    }
  }
  return value && typeof value === 'object' && !Array.isArray(value)
    ? value
    : {}
}

function normalizeBoolean(value) {
  return value === true || value === 1 || value === '1' || value === 'true'
}

function readPath(value, path) {
  return path.split('.').reduce((current, key) => {
    const record = parseRecord(current)
    return record[key]
  }, value)
}

function firstDefined(values) {
  return values.find(value => value !== undefined && value !== null)
}

export function getIpLocaleCurrencyConfig(storeInfo) {
  const enabled = firstDefined([
    readPath(storeInfo, `storePageInfo.otherJson.${AUTO_SELECT_LOCALE_CURRENCY_BY_IP_KEY}`),
    readPath(storeInfo, `storePageInfo.${AUTO_SELECT_LOCALE_CURRENCY_BY_IP_KEY}`),
    readPath(storeInfo, `offiaccountFollowData.otherJson.${AUTO_SELECT_LOCALE_CURRENCY_BY_IP_KEY}`),
    readPath(storeInfo, `offiaccountFollowUrlData.otherJson.${AUTO_SELECT_LOCALE_CURRENCY_BY_IP_KEY}`),
    readPath(storeInfo, `otherJson.${AUTO_SELECT_LOCALE_CURRENCY_BY_IP_KEY}`),
    readPath(storeInfo, `mallSiteConfig.${AUTO_SELECT_LOCALE_CURRENCY_BY_IP_KEY}`),
  ])
  const apiUrl = firstDefined([
    readPath(storeInfo, `storePageInfo.otherJson.${IP_LOCALE_API_URL_KEY}`),
    readPath(storeInfo, `storePageInfo.${IP_LOCALE_API_URL_KEY}`),
    readPath(storeInfo, `offiaccountFollowData.otherJson.${IP_LOCALE_API_URL_KEY}`),
    readPath(storeInfo, `offiaccountFollowUrlData.otherJson.${IP_LOCALE_API_URL_KEY}`),
    readPath(storeInfo, `otherJson.${IP_LOCALE_API_URL_KEY}`),
    readPath(storeInfo, `mallSiteConfig.${IP_LOCALE_API_URL_KEY}`),
  ])

  return {
    enabled: normalizeBoolean(enabled),
    apiUrl: String(apiUrl || DEFAULT_IP_LOCALE_API_URL).trim() || DEFAULT_IP_LOCALE_API_URL,
  }
}

export function mapCountryCodeToLocaleCurrency(countryCode) {
  const normalizedCountryCode = String(countryCode || '').trim().toUpperCase()
  if (normalizedCountryCode === 'CN') {
    return { language: 'zh-CN', currencyCode: 'CNY' }
  }
  if (normalizedCountryCode === 'TW') {
    return { language: 'zh-TW', currencyCode: 'TWD' }
  }
  return { language: 'en', currencyCode: 'USD' }
}

function getCountryCodeFromResponse(value) {
  return String(
    value?.country_code
    ?? value?.countryCode
    ?? value?.country_code2
    ?? value?.data?.country_code
    ?? value?.data?.countryCode
    ?? '',
  ).trim().toUpperCase()
}

export function buildIpLocaleApiUrl(apiUrl, clientIp = '') {
  const normalizedApiUrl = String(apiUrl || DEFAULT_IP_LOCALE_API_URL).trim()
    || DEFAULT_IP_LOCALE_API_URL
  const normalizedClientIp = String(clientIp || '').trim()

  if (!normalizedClientIp)
    return normalizedApiUrl

  if (normalizedApiUrl.includes('{ip}')) {
    return normalizedApiUrl.replaceAll('{ip}', encodeURIComponent(normalizedClientIp))
  }

  try {
    const url = new URL(normalizedApiUrl)
    if (url.hostname === 'api.ip.sb' && /^\/geoip\/?$/.test(url.pathname)) {
      url.pathname = `${url.pathname.replace(/\/$/, '')}/${encodeURIComponent(normalizedClientIp)}`
      return url.toString()
    }
  }
  catch {
    return normalizedApiUrl
  }

  return normalizedApiUrl
}

export async function fetchIpCountryCode(apiUrl, { clientIp = '' } = {}) {
  const requestUrl = buildIpLocaleApiUrl(apiUrl, clientIp)
  if (activeCountryCodeRequests.has(requestUrl)) {
    return activeCountryCodeRequests.get(requestUrl)
  }

  const requestPromise = (async () => {
    const controller = typeof AbortController === 'undefined' ? null : new AbortController()
    const timeout = setTimeout(() => controller?.abort(), 5000)
    try {
      const response = await fetch(requestUrl, {
        method: 'GET',
        headers: { accept: 'application/json' },
        signal: controller?.signal,
      })
      if (!response.ok) {
        throw new Error(`IP locale request failed: ${response.status}`)
      }
      return getCountryCodeFromResponse(await response.json())
    }
    finally {
      clearTimeout(timeout)
    }
  })()
  activeCountryCodeRequests.set(requestUrl, requestPromise)

  try {
    return await requestPromise
  }
  finally {
    activeCountryCodeRequests.delete(requestUrl)
  }
}

export async function resolveInitialLocaleCurrencyPreference({
  storeInfo,
  savedLanguage = '',
  savedCurrencyCode = '',
  fetchCountryCode = fetchIpCountryCode,
}) {
  const config = getIpLocaleCurrencyConfig(storeInfo)
  if (!config.enabled) {
    return null
  }

  const normalizedLanguage = String(savedLanguage || '').trim()
  const normalizedCurrencyCode = String(savedCurrencyCode || '').trim().toUpperCase()
  if (normalizedLanguage && normalizedCurrencyCode) {
    return {
      language: normalizedLanguage,
      currencyCode: normalizedCurrencyCode,
      source: 'cache',
    }
  }

  let detectedPreference
  try {
    detectedPreference = mapCountryCodeToLocaleCurrency(
      await fetchCountryCode(config.apiUrl),
    )
  }
  catch {
    detectedPreference = mapCountryCodeToLocaleCurrency('')
  }

  return {
    language: normalizedLanguage || detectedPreference.language,
    currencyCode: normalizedCurrencyCode || detectedPreference.currencyCode,
    source: 'ip',
  }
}

export function getSavedCurrencyCode(storage) {
  try {
    const currentCurrency = JSON.parse(storage.getItem(CURRENCY_CACHE_KEY) || 'null')
    return String(currentCurrency?.toCurrencyCode || '').trim().toUpperCase()
  }
  catch {
    return ''
  }
}
